import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { ApiError, errorMessages } from '../../api/client'
import {
  accountEditQuery,
  accountEventsQuery,
  accountQuery,
  accountTabsQuery,
  changePassword,
  newAccountFormQuery,
  permissionsQuery,
  privacyQuery,
  removePhoto,
  saveAccount,
  savePermissions,
  savePrivacy,
  saveVideo,
  uploadPhoto,
  videoQuery,
  type AccountDetails,
  type AccountEditForm,
  type IamManager,
} from '../../api/settingsUsers'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { CheckField, FormActions, FormRow, HalfField } from '../../ui/FormGrid'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { esc, failed, tableRequest, whenText } from './shared'
import { initialPermissions, PermissionsGrid, type PermissionMap } from './PermissionsGrid'
import { AccountRoleChanges, AccountRoles, RaSection } from './AccountRoles'
import { AccountTicketGroups } from './AccountTicketGroups'
import { HourlyRate } from './HourlyRate'
import { AccessLevelLegend } from './AccessLevelLegend'
import { accessHelp, accessLabel, offeredAccessLevels } from './accessLevels'
import './RolesAccess.css'

/**
 * Fisa unui cont: banda de actiuni din stanga si panoul din dreapta.
 *
 * Perechea veche e `active/tabList.php` (randul desfasurat al tabelului, cu
 * `ul.action_tabs`) plus cele opt machete pe care le incarca:
 * `details.php`, `editForm.php`, `changePhoto.php`, `changePassword.php`,
 * `privacySettings.php`, `events.php`, `permissions.php`, `video_conference.php`.
 *
 * FILELE VIN DE PE SERVER, filtrate pe drepturi (`getActiveTabs`). Ecranul nu
 * decide singur ce se vede.
 */
export function AccountWorkspace({ userId }: { userId: number }) {
  const tabs = useQuery(accountTabsQuery(userId))
  const [current, setCurrent] = useState('')

  useEffect(() => {
    if (!tabs.data || tabs.data.tabs.length === 0) return
    setCurrent((prev) => (prev !== '' && tabs.data.tabs.some((t) => t.id === prev) ? prev : tabs.data.tabs[0].id))
  }, [tabs.data])

  if (tabs.error instanceof ApiError && tabs.error.status === 403) {
    return <div className="alert alert-info">You do not have access to this account.</div>
  }
  if (!tabs.data) return <Spinner />

  if (tabs.data.tabs.length === 0) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> There is nothing here you may see.
      </div>
    )
  }

  const open = tabs.data.tabs.find((t) => t.id === current) ?? tabs.data.tabs[0]

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.data.tabs.map((tab) => (
            <li key={tab.id}>
              <a
                href={`/app/settings/users_accounts/active#${tab.id}`}
                className={tab.id === open.id ? 'action_button active' : 'action_button'}
                onClick={(event) => {
                  event.preventDefault()
                  setCurrent(tab.id)
                }}
              >
                {tab.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {open.id === 'details' && <DetailsTab userId={userId} />}
          {open.id === 'edit' && <EditTab userId={userId} />}
          {open.id === 'roles' && <RolesTab userId={userId} />}
          {open.id === 'photo' && <PhotoTab userId={userId} />}
          {open.id === 'password' && <PasswordTab userId={userId} />}
          {open.id === 'privacy' && <PrivacyTab userId={userId} />}
          {open.id === 'events' && <EventsTab userId={userId} />}
          {open.id === 'permissions' && <PermissionsTab userId={userId} />}
          {open.id === 'video' && <VideoTab userId={userId} />}
        </div>
      </div>
    </div>
  )
}

/* =======================================================================
 * Details (active/details.php)
 * ==================================================================== */

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="col-md-6">
      <div className="row">
        <div className="col-xs-6" style={{ marginBottom: 5 }}>
          <label>{label}</label>
        </div>
        <div className="col-xs-6">{children}</div>
      </div>
    </div>
  )
}

function DetailsTab({ userId }: { userId: number }) {
  const page = useQuery(accountQuery(userId))

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  const user = page.data.user

  /*
   * "Username" se arata NUMAI cand e altul decat emailul - adica la conturile
   * facute inainte de 4 septembrie 2026. La cele noi cele doua sunt acelasi
   * lucru, si un rand care repeta emailul de deasupra doar intreaba omul de ce
   * sunt doua. Nu dispare de tot: cu numele vechi se poate inca intra.
   */
  const showsUsername = user.username.trim().toLowerCase() !== user.email.trim().toLowerCase()

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ width: 200, verticalAlign: 'top' }}>
        {user.avatarUrl !== '' ? (
          <img
            style={{ width: 140, height: 140 }}
            className="img-thumbnail"
            src={user.avatarUrl}
            alt=""
            onError={(event) => {
              event.currentTarget.style.display = 'none'
            }}
          />
        ) : (
          <div
            style={{
              overflow: 'hidden',
              fontSize: 110,
              textAlign: 'center',
              color: '#ddd',
              float: 'none',
              width: 140,
              height: 140,
              display: 'block',
              borderRadius: '50%',
              border: '5px solid rgb(221, 221, 221)',
            }}
          >
            <i style={{ top: 8 }} className="fas fa-user" aria-hidden="true"></i>
          </div>
        )}
      </div>

      <div style={{ flex: 1 }}>
        <div className="row">
          <div className="col-md-12">
            <h4 className="bordered">
              <i className="fa fa-address-card" aria-hidden="true"></i> User details
            </h4>
          </div>
          <Field label="Firstname">{user.firstname}</Field>
          <Field label="Lastname">{user.lastname}</Field>
          {showsUsername ? (
            <div className="col-md-6">
              <div className="row">
                <div className="col-xs-6" style={{ marginBottom: 5 }}>
                  <label>Username</label>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    older login name, still works
                  </div>
                </div>
                <div className="col-xs-6">
                  <strong className="color-blue">{user.username}</strong>
                </div>
              </div>
            </div>
          ) : null}
          <Field label="Email">{user.email}</Field>
          <Field label="Phone">{user.phone}</Field>
          <Field label="Mobile Phone">{user.mobilePhone}</Field>
          <Field label="IT Manager">{user.itManager ? 'Yes' : 'No'}</Field>
          <Field label="Group">{user.departmentName}</Field>
          <Field label="Position">{user.position}</Field>
          <Field label="Country">
            <i className="fa fa-globe" aria-hidden="true"></i> {user.countryName}
          </Field>
          <Field label="Onsite">
            <i className="fa fa-map-pin" aria-hidden="true"></i> {user.onSite === 1 ? 'Yes' : 'No'}
          </Field>
          <Field label="GPS tracking">
            <i className="fa fa-map-marker" aria-hidden="true"></i> {user.gpsTracking === 1 ? 'Yes' : 'No'}
          </Field>
          <Field label="Car">
            <i className="fa fa-car" aria-hidden="true"></i>{' '}
            {user.car === 0 ? 'No' : user.car === 1 ? 'Personal' : 'Work'}
          </Field>
          <Field label="User Created">
            <i className="far fa-calendar-check" aria-hidden="true"></i> {user.created}
          </Field>
          <Field label="Address">{user.address}</Field>
          <Field label="Internal Rank">{user.internalRank}</Field>
          {user.iamManager ? <IamManagerField manager={user.iamManager} /> : null}
        </div>
      </div>
    </div>
  )
}

/**
 * "Manager (from IAM)": managerul trimis de IAM prin provizionare. Numai de
 * citit - il schimba doar IAM-ul. Nelegat = contul managerului nu exista (inca)
 * la noi; se leaga singur cand IAM-ul il creeaza.
 */
function IamManagerField({ manager }: { manager: IamManager }) {
  const name = manager.name ?? manager.username ?? manager.external_id ?? ''
  const detail = manager.username && manager.username !== name ? manager.username : null

  return (
    <Field label="Manager (from IAM)">
      <span data-testid="iam-manager">
        <strong>{name}</strong>
        {detail ? <span className="text-muted"> ({detail})</span> : null}{' '}
        {manager.linked ? (
          <span className="label label-success" title="The manager's account exists here.">
            linked
          </span>
        ) : (
          <span
            className="label label-default"
            title="No account here matches the manager yet; it links on its own when the IAM provisions it."
          >
            not linked
          </span>
        )}
        {manager.linked && manager.active === false ? (
          <>
            {' '}
            <span className="label label-warning" title="The manager's account is blocked or has no access.">
              inactive
            </span>
          </>
        ) : null}
      </span>
    </Field>
  )
}

/* =======================================================================
 * FORMA COMUNA A PANOURILOR
 * =======================================================================
 *
 * Cele cinci panouri cu formular - Edit user, Change Photo, Change Password,
 * Privacy Settings, Video Conference - erau desenate fiecare altfel, fiindca
 * asa erau si machetele vechi din care au fost portate:
 *
 *   - `editForm.php` punea campurile pe doua coloane (`.form-group.row` cu
 *     doua `col-md-6`), cu eticheta DEASUPRA campului;
 *   - `changePassword.php` si primele trei campuri din `privacySettings.php`
 *     le lasau pe toata latimea panoului - 942px, fata de 456px in Edit user;
 *   - cele trei bifari din `editForm.php` aveau trei forme diferite intre ele,
 *     iar una purta `class="form-control"` PE O BIFA, ceea ce o intindea la
 *     294x34px, cat un camp de text.
 *
 * Ecranele astea se deschid unul dupa altul din aceeasi banda de file, deci
 * saritura se vedea la fiecare apasare. Aici toate trec prin aceleasi trei
 * bucati, si toate campurile cad pe aceeasi grila: coloana din stanga, coloana
 * din dreapta, eticheta deasupra campului.
 *
 * BUCATILE AU PLECAT DE AICI in `ui/FormGrid.tsx` pe 10 septembrie 2026, cand
 * la aceeasi forma a fost adus si ecranul My Account. Motivul scris acolo e
 * chiar cel din capul acestui comentariu: doua copii ale unei asezari sunt
 * felul in care panourile se departeaza unul de altul.
 *
 * CE NU SE SCHIMBA: ce campuri sunt, in ce ordine, ce trimit si cine le vede.
 * Numai asezarea. Clasele raman ale portalului (`form-group`, `row`,
 * `col-md-*`, `form-control`, `control-label`, `help-block`) - nu se adauga
 * nici o foaie de stil noua.
 *
 * `details.php` ramane cu eticheta LANGA valoare: e o citire, nu un formular,
 * si cele saptesprezece randuri ale lui puse pe doua etaje fiecare ar face un
 * panou de doua ori mai inalt fara sa spuna nimic in plus. `permissions.php`
 * are grila lui, din alt motiv scris in `PermissionsGrid.tsx`.
 */

/* =======================================================================
 * Edit user (active/editForm.php)
 * ==================================================================== */

function EditTab({ userId }: { userId: number }) {
  const account = useAccountForm(userId)
  if (account.state !== 'ready') return account.view
  const { page, form, set, submit, saving, errors } = account

  return (
    <>
      <form role="form" id="edit_user_form" method="post" onSubmit={submit}>
        <FormErrors errors={errors} />

        <FormRow>
          <CheckField
            id="collaboratorBTN"
            label="Collaborator"
            className="collaborator"
            checked={form.collaborator}
            onChange={(on) => set('collaborator', on)}
          />
        </FormRow>

        {/*
      "Username" a plecat din formular: numele de intrare e EMAILUL, si se
      scrie pe server la fiecare salvare (`Services\Auth\LoginHandle`). Campul
      ramanea aici doar ca sa fie schimbat din greseala.
    */}

        <FormRow>
          <HalfField label="First Name" required htmlFor="edit_firstname">
            <input
              id="edit_firstname"
              className="form-control"
              type="text"
              required
              value={form.firstname}
              onChange={(e) => set('firstname', e.target.value)}
            />
          </HalfField>
          <HalfField label="Last Name" required htmlFor="edit_lastname">
            <input
              id="edit_lastname"
              className="form-control"
              type="text"
              required
              value={form.lastname}
              onChange={(e) => set('lastname', e.target.value)}
            />
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField
            label="Email"
            required
            htmlFor="edit_email"
            help="This is the login. Changing it changes what the person signs in with."
          >
            <input
              id="edit_email"
              placeholder="Email"
              className="form-control"
              type="email"
              required
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
            />
          </HalfField>
          <HalfField label="Internal Rank" htmlFor="edit_internal_rank">
            <input
              id="edit_internal_rank"
              placeholder="Internal Rank"
              className="form-control"
              type="text"
              value={form.internalRank}
              onChange={(e) => set('internalRank', e.target.value)}
            />
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField label="Phone" htmlFor="edit_phone">
            <input
              id="edit_phone"
              className="form-control"
              type="text"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
            />
          </HalfField>
          <HalfField label="Mobile Phone" htmlFor="edit_mobile_phone">
            <input
              id="edit_mobile_phone"
              className="form-control"
              type="text"
              value={form.mobilePhone}
              onChange={(e) => set('mobilePhone', e.target.value)}
            />
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField label="Company" required htmlFor="edit_company">
            <select
              id="edit_company"
              className="form-control"
              required
              value={String(form.companyId)}
              onChange={(e) => set('companyId', Number(e.target.value))}
            >
              {page.companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </HalfField>
          <HalfField label="Group" htmlFor="edit_department">
            <select
              id="edit_department"
              className="form-control"
              value={form.departmentId > 0 ? String(form.departmentId) : ''}
              onChange={(e) => set('departmentId', Number(e.target.value))}
            >
              <option value="">Select...</option>
              {page.departments.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </HalfField>
        </FormRow>

        {/* Access si Primary role stau in fila "Roles & access" din banda din
        stanga - aceeasi salvare, acelasi capat. */}
        <FormRow>
          <HalfField label="Occupation" htmlFor="edit_occupation">
            <input
              id="edit_occupation"
              className="form-control"
              type="text"
              value={form.position}
              onChange={(e) => set('position', e.target.value)}
            />
          </HalfField>
          <HalfField label="GPS tracking" htmlFor="edit_gps_tracking">
            <select
              id="edit_gps_tracking"
              className="form-control"
              value={String(form.gpsTracking)}
              onChange={(e) => set('gpsTracking', Number(e.target.value))}
            >
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </HalfField>
        </FormRow>

        {/* Onsite si Car impart jumatatea din dreapta: doua raspunsuri de un
        cuvant nu au nevoie de 456px fiecare, iar marginile randului raman
        exact acolo unde sunt si la celelalte. */}
        <FormRow>
          <HalfField label="Country" htmlFor="edit_country">
            <select
              id="edit_country"
              className="form-control"
              value={form.countryId > 0 ? String(form.countryId) : ''}
              onChange={(e) => set('countryId', Number(e.target.value))}
            >
              <option value="">Select...</option>
              {page.countries.map((country) => (
                <option key={country.id} value={country.id}>
                  ({country.prefix}) {country.name}
                </option>
              ))}
            </select>
          </HalfField>
          <HalfField label="Onsite" span={3} htmlFor="edit_onsite">
            <select
              id="edit_onsite"
              className="form-control"
              value={String(form.onSite)}
              onChange={(e) => set('onSite', Number(e.target.value))}
            >
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </HalfField>
          <HalfField label="Car" span={3} htmlFor="edit_car">
            <select
              id="edit_car"
              className="form-control"
              value={String(form.car)}
              onChange={(e) => set('car', Number(e.target.value))}
            >
              <option value="0">No</option>
              <option value="1">Personal</option>
              <option value="2">Work</option>
            </select>
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField label="Address" htmlFor="edit_address">
            <textarea
              id="edit_address"
              className="form-control"
              rows={1}
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
            />
          </HalfField>
        </FormRow>

        <FormRow>
          <CheckField
            id="is_it_manager"
            label="IT Manager"
            className="icheck-user"
            checked={form.itManager}
            onChange={(on) => set('itManager', on)}
          />
          <CheckField
            id="has_quick_chat_support"
            label="Enable quick chat support"
            checked={form.quickChatSupport}
            onChange={(on) => set('quickChatSupport', on)}
          />
        </FormRow>

        <SaveButton saving={saving} />
      </form>
      {/* Tariful orar (numai v4): costul pe tehnician din Rapoarte. */}
      <HourlyRate type="user" id={userId} />
    </>
  )
}

/* =======================================================================
 * Roles & access (numai v4)
 * =======================================================================
 *
 * Fila proprie in banda din stanga, imediat dupa "Edit user", cu acelasi
 * drept (`settings.users_active_edit.read`). Nivelul de acces si rolul
 * principal se salveaza prin ACELASI capat ca Edit user, cu restul campurilor
 * trimise neschimbate, asa cum au venit din formularul contului - altfel o
 * schimbare de rol ar goli numele sau telefonul.
 */

function RolesTab({ userId }: { userId: number }) {
  const account = useAccountForm(userId)

  return (
    <div className="od-ra">
      <RaSection title="Access in the platform">
        {account.state !== 'ready' ? (
          account.view
        ) : (
          <form role="form" id="edit_user_access_form" method="post" onSubmit={account.submit}>
            <FormErrors errors={account.errors} />
            <RolesAccessFields page={account.page} form={account.form} set={account.set} />
            <div className="od-ra-actions">
              <button className="btn btn-success" type="submit" disabled={account.saving}>
                {account.saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </RaSection>
      {/* Rolurile suplimentare (numai v4): uniunea drepturilor cu rolul principal. */}
      <AccountRoles userId={userId} />
      {/* Grupurile de tichete si ce tichete vede omul (numai v4). */}
      <AccountTicketGroups userId={userId} />
      {/* Jurnalul rolurilor, la capat. */}
      <AccountRoleChanges userId={userId} />
    </div>
  )
}

type SetField = <K extends keyof AccountDetails>(key: K, value: AccountDetails[K]) => void

/**
 * Nivelul de acces si rolul principal, pe doua coloane egale (una sub alta pe
 * ecran ingust). Treptele vin de pe server (`accessLevels`); una ascunsa
 * (GPS Dispatcher) apare numai daca e treapta salvata a contului, ca o
 * salvare a rolului sa nu i-o schimbe.
 */
function RolesAccessFields({ page, form, set }: { page: AccountEditForm; form: AccountDetails; set: SetField }) {
  const offered = offeredAccessLevels(page.accessLevels, page.user.accessId)
  /* De unde vine nivelul (19 octombrie 2026): din rolul principal sau de mana.
     null = de mana, ca azi. */
  const fromRole = form.accessSource === 'role'
  const role = page.roles.find((r) => r.id === form.roleId)
  const roleLevel = role?.defaultAccess ?? null
  /* "From role" cere un rol care da un nivel - afara de contul care il urmeaza deja. */
  const mayFollow = roleLevel !== null || page.user.accessSource === 'role'
  const closed = page.user.accessId === 0
  /* Urmand rolul, selectul arata ce va da rolul (un cont inchis ramane inchis). */
  const shown = fromRole && roleLevel !== null && !closed ? roleLevel : form.accessId
  const current = offered.find((level) => level.value === shown)

  return (
    <div className="od-ra-grid">
      <div className="od-ra-field">
        {/* Legenda sta langa eticheta, nu in ea: altfel textul ei ar intra in numele selectului. */}
        <div className="od-ra-label-row">
          <label className="control-label" htmlFor="edit_access">
            Access level <span className="required">*</span>
          </label>
          <AccessLevelLegend levels={offered} />
        </div>
        <div className="od-ra-source" role="radiogroup" aria-label="Where the access level comes from">
          <label className={`od-ra-source-option${mayFollow ? '' : ' od-ra-source-off'}`}>
            <input
              type="radio"
              name="access_source"
              value="role"
              checked={fromRole}
              disabled={!mayFollow}
              onChange={() => set('accessSource', 'role')}
            />{' '}
            {!role
              ? 'From role — no primary role chosen'
              : roleLevel !== null
                ? `From role — ${accessLabel(roleLevel)} (from role ${role.name})`
                : `From role — the role “${role.name}” has no access level set`}
          </label>
          <label className="od-ra-source-option">
            <input
              type="radio"
              name="access_source"
              value="manual"
              checked={!fromRole}
              onChange={() => set('accessSource', 'manual')}
            />{' '}
            Set by hand
          </label>
        </div>
        {role && roleLevel === null ? (
          <a className="od-ra-source-link" href="/app/settings/users_accounts/userRoles">
            Set it in Roles and Resources
          </a>
        ) : null}
        <select
          id="edit_access"
          className="form-control"
          required
          disabled={fromRole}
          value={String(shown)}
          onChange={(e) => set('accessId', Number(e.target.value))}
        >
          {closed && fromRole ? <option value="0">Closed</option> : null}
          {offered.map((level) => (
            <option key={level.value} value={level.value}>
              {level.label}
            </option>
          ))}
        </select>
        <span className="help-block">
          {current ? accessHelp(current) : 'Which companies the person sees.'}
          {fromRole && closed ? ' The account is closed and stays closed while it follows the role.' : ''}
        </span>
      </div>
      <div className="od-ra-field">
        <label className="control-label" htmlFor="edit_role">
          Primary role <span className="required">*</span>
        </label>
        <select
          id="edit_role"
          className="form-control"
          required
          value={form.roleId > 0 ? String(form.roleId) : ''}
          onChange={(e) => set('roleId', Number(e.target.value))}
        >
          <option value="">Select...</option>
          {page.roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
        <span className="help-block">What the person can see and do in the portal.</span>
      </div>
    </div>
  )
}

/**
 * Formularul contului, comun lui Edit user si Roles & access: aceleasi date
 * (`accountEditQuery`), aceeasi salvare, aceeasi incarcatura trimisa.
 */
function useAccountForm(userId: number) {
  const client = useQueryClient()
  const page = useQuery(accountEditQuery(userId))
  const [form, setForm] = useState<AccountDetails | null>(null)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  useEffect(() => {
    if (page.data) setForm(page.data.user)
  }, [page.data])

  if (page.error) {
    return { state: 'failed' as const, view: <div className="alert alert-danger">{failed(page.error)}</div> }
  }
  if (!page.data || !form) return { state: 'loading' as const, view: <Spinner /> }

  const set: SetField = (key, value) => setForm((prev) => (prev ? { ...prev, [key]: value } : prev))

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setErrors([])
    void saveAccount(userId, {
      firstname: form.firstname,
      lastname: form.lastname,
      email: form.email,
      internal_rank: form.internalRank,
      phone: form.phone,
      mobile_phone: form.mobilePhone,
      company: form.companyId,
      department: form.departmentId,
      access: form.accessId,
      /* '' = nu s-a ales nimic (Edit user, sau server vechi): sursa ramane cum era. */
      access_source: form.accessSource ?? '',
      occupation: form.position,
      role: form.roleId,
      gps_tracking: form.gpsTracking,
      country: form.countryId,
      onsite: form.onSite,
      car: form.car,
      address: form.address,
      collaborator: form.collaborator ? 'on' : '',
      it_manager: form.itManager ? 1 : 0,
      has_quick_chat_support: form.quickChatSupport ? 'on' : '',
    })
      .then(() => {
        toast('Changes saved', 'Success')
        void client.invalidateQueries({ queryKey: ['settings', 'users', 'accounts'] })
      })
      .catch((error: unknown) => {
        const messages = errorMessages(error)
        setErrors(messages.length > 0 ? messages : [failed(error)])
        toast(failed(error), 'Error', 'error')
      })
      .finally(() => setSaving(false))
  }

  return { state: 'ready' as const, page: page.data, form, set, submit, saving, errors }
}

function FormErrors({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null
  return (
    <div className="form-errors">
      {errors.map((message) => (
        <div className="alert alert-danger" key={message}>
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {message}
        </div>
      ))}
    </div>
  )
}

function SaveButton({ saving }: { saving: boolean }) {
  return (
    <FormActions>
      <button className="btn btn-success" type="submit" disabled={saving}>
        {saving ? 'Saving...' : 'Save changes'}
      </button>
    </FormActions>
  )
}

/* =======================================================================
 * Change Photo (active/changePhoto.php)
 * ==================================================================== */

function PhotoTab({ userId }: { userId: number }) {
  const client = useQueryClient()
  const page = useQuery(accountQuery(userId))
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'users', 'accounts', userId] })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!file) {
      toast('Choose a picture first.', 'Error', 'error')
      return
    }
    const form = new FormData()
    form.append('photo', file)
    setBusy(true)
    void uploadPhoto(userId, form)
      .then(() => {
        toast('Picture saved', 'Success')
        setFile(null)
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  const drop = () => {
    if (!confirm('Remove the avatar?')) return
    setBusy(true)
    void removePhoto(userId)
      .then(() => {
        toast('Avatar removed', 'Success')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <form role="form" id="change_photo_form" method="post" onSubmit={submit}>
      {/* Poza sta in coloana din stanga, exact acolo unde incep etichetele
          celorlalte panouri; alesul fisierului e un camp ca oricare altul. */}
      {page.data?.user.avatarUrl ? (
        <FormRow>
          <div className="col-md-6">
            <img
              className="img-thumbnail"
              style={{ width: 140, height: 140 }}
              src={page.data.user.avatarUrl}
              alt=""
              onError={(event) => {
                event.currentTarget.style.display = 'none'
              }}
            />
          </div>
        </FormRow>
      ) : null}

      <FormRow>
        <HalfField label="Upload Picture" htmlFor="change_photo_file">
          {/* Fara `form-control`: pe o casuta de fisier inaltimea fixa de 34px
              taie butonul nativ al browserului. Coloana da alinierea. */}
          <input
            id="change_photo_file"
            type="file"
            accept="image/png,image/jpeg,image/gif"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </HalfField>
      </FormRow>
      <FormActions>
        <button className="btn btn-success" type="submit" disabled={busy}>
          Save Changes
        </button>{' '}
        <button className="btn btn-danger remove-photo" type="button" onClick={drop} disabled={busy}>
          Remove Avatar
        </button>
      </FormActions>
    </form>
  )
}

/* =======================================================================
 * Change Password (active/changePassword.php)
 * ==================================================================== */

function PasswordTab({ userId }: { userId: number }) {
  /* Regula de parola vine de la server (`PasswordPolicy::hint()`), ca sa fie
     scrisa intr-un singur loc - la fel ca in macheta veche. */
  const form = useQuery(newAccountFormQuery)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void changePassword(userId, { new_password: password, confirm_password: confirm })
      .then((answer) => {
        toast(
          answer.mailed ? 'Password changed. The user was emailed.' : 'Password changed, but the email did not go out.',
          'Success',
        )
        setPassword('')
        setConfirm('')
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <form role="form" id="change_password_form" method="post" onSubmit={submit}>
      <FormRow>
        {/* Regula se spune INAINTE, nu ca refuz dupa apasarea butonului. */}
        <HalfField label="New Password" required htmlFor="new_password" help={form.data?.passwordHint ?? ''}>
          <input
            id="new_password"
            className="form-control"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </HalfField>
        <HalfField label="Confirm New Password" required htmlFor="confirm_password">
          <input
            id="confirm_password"
            className="form-control"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </HalfField>
      </FormRow>
      <div className="note note-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> The user will receive an email with the new password.
      </div>
      <FormActions>
        <button className="btn btn-success" type="submit" disabled={busy}>
          Change Password
        </button>
      </FormActions>
    </form>
  )
}

/* =======================================================================
 * Privacy Settings (active/privacySettings.php)
 * ==================================================================== */

function PrivacyTab({ userId }: { userId: number }) {
  const client = useQueryClient()
  const page = useQuery(privacyQuery(userId))
  const [form, setForm] = useState<{
    timezone: string
    signature: string
    plan: string
    imei: string
    imei2: string
    notifications: boolean
    autoAssign: boolean
  } | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!page.data) return
    const s = page.data.settings
    setForm({
      timezone: s.timezone > 0 ? String(s.timezone) : '',
      signature: s.signatureTemplate > 0 ? String(s.signatureTemplate) : '',
      plan: s.workHourPlan > 0 ? String(s.workHourPlan) : '',
      imei: s.imei,
      imei2: s.imei2,
      notifications: s.enableNotifications,
      autoAssign: s.autoAssign,
    })
  }, [page.data])

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data || !form) return <Spinner />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void savePrivacy(userId, {
      timezone: form.timezone,
      signature_template: form.signature,
      work_hour_plan: form.plan,
      imei: form.imei,
      imei2: form.imei2,
      enable_notifications: form.notifications ? 'on' : '',
      auto_assign: form.autoAssign ? 'on' : '',
    })
      .then(() => {
        toast('Changes saved', 'Success')
        void client.invalidateQueries({ queryKey: ['settings', 'users', 'accounts', userId, 'privacy'] })
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <form role="form" id="privacy_settings_form" method="post" onSubmit={submit}>
      <FormRow>
        <HalfField label="Timezone" htmlFor="privacy_timezone">
          <select
            id="privacy_timezone"
            className="form-control"
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
          >
            <option value="">Select your timezone...</option>
            {page.data.timezones.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
              </option>
            ))}
          </select>
        </HalfField>
        <HalfField label="Signature" htmlFor="privacy_signature">
          <select
            id="privacy_signature"
            className="form-control"
            value={form.signature}
            onChange={(e) => setForm({ ...form, signature: e.target.value })}
          >
            <option value="">Select...</option>
            {page.data.signatures.map((signature) => (
              <option key={signature.id} value={signature.id}>
                {signature.name}
              </option>
            ))}
          </select>
        </HalfField>
      </FormRow>
      <FormRow>
        <HalfField label="Work Hour Plan" htmlFor="privacy_plan">
          <select
            id="privacy_plan"
            className="form-control"
            value={form.plan}
            onChange={(e) => setForm({ ...form, plan: e.target.value })}
          >
            <option value="">Select...</option>
            {page.data.plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </select>
        </HalfField>
      </FormRow>
      <FormRow>
        <HalfField label="IMEI" htmlFor="privacy_imei">
          <input
            id="privacy_imei"
            className="form-control"
            type="text"
            value={form.imei}
            onChange={(e) => setForm({ ...form, imei: e.target.value })}
          />
        </HalfField>
        <HalfField
          label="IMEI 2"
          htmlFor="privacy_imei2"
          help={
            <>
              <i className="fas fa-info-circle"></i> Required for dual sim phones
            </>
          }
        >
          <input
            id="privacy_imei2"
            className="form-control"
            type="text"
            value={form.imei2}
            onChange={(e) => setForm({ ...form, imei2: e.target.value })}
          />
        </HalfField>
      </FormRow>
      <FormRow>
        <CheckField
          id="enable_notifications"
          label="Enable notifications"
          className="enable_notifications"
          checked={form.notifications}
          onChange={(on) => setForm({ ...form, notifications: on })}
        />
        <CheckField
          id="auto_assign"
          label="Auto assign"
          className="auto_assign"
          checked={form.autoAssign}
          onChange={(on) => setForm({ ...form, autoAssign: on })}
        />
      </FormRow>
      <FormActions>
        <button className="btn btn-success" type="submit" disabled={busy}>
          Save Changes
        </button>
      </FormActions>
    </form>
  )
}

/* =======================================================================
 * Events (active/events.php + UserEventsModel)
 * ==================================================================== */

const EVENT_ORDER_NAMES = ['event_date', 'event_message', 'user']

export function eventColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: 'Date',
      titleHtml: '<i class="far fa-calendar"></i> Date',
      orderable: true,
      visible: true,
      width: 150,
      className: 'td-small a-left',
      html: (row) => esc(whenText(row.event_date)),
    },
    {
      index: 1,
      title: 'Message',
      orderable: true,
      visible: true,
      className: 'td-small a-left',
      html: (row) => esc(String(row.event_message ?? '')),
    },
    {
      index: 2,
      title: 'User',
      orderable: true,
      visible: true,
      width: 200,
      className: 'td-small a-left',
      html: (row) =>
        row.authorFirstname
          ? esc(`${String(row.authorFirstname)} ${String(row.authorName ?? '')}`)
          : '<strong>System</strong>',
    },
  ]
}

function EventsTab({ userId }: { userId: number }) {
  const [state, setState] = useTableState(`settings-user-events-${userId}`, {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => eventColumns(), [])
  const table = useQuery(accountEventsQuery(userId, tableRequest(state, EVENT_ORDER_NAMES, 'event_date')))

  return (
    <DataTable
      id={`settings-user-events-${userId}`}
      columns={columns}
      rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
      total={table.data?.recordsFiltered ?? 0}
      fetching={table.isFetching}
      error={table.error ? failed(table.error) : null}
      onRetry={() => void table.refetch()}
      state={state}
      onState={setState}
      rowId={(row) => `rowd_${Number(row.id)}`}
      emptyHint="Nothing has happened on this account yet."
    />
  )
}

/* =======================================================================
 * Permissions (active/permissions.php)
 * ==================================================================== */

function PermissionsTab({ userId }: { userId: number }) {
  const page = useQuery(permissionsQuery(userId))
  const [chosen, setChosen] = useState<PermissionMap>({})
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (page.data) setChosen(initialPermissions(page.data.resources))
  }, [page.data])

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  const submit = () => {
    setBusy(true)
    void savePermissions(userId, chosen)
      .then(() => toast('Changes saved', 'Success'))
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <>
      {page.data.iamLocked ? (
        <div className="alert alert-info small" data-testid="permissions-iam-locked">
          {page.data.iamLocked} Personal permissions set here are removed at the next sign-in.
        </div>
      ) : null}
      <PermissionsGrid
        modules={page.data.modules}
        resources={page.data.resources}
        chosen={chosen}
        onChange={setChosen}
        onSubmit={submit}
        saving={busy}
      />
    </>
  )
}

/* =======================================================================
 * Video Conference (active/video_conference.php)
 * ==================================================================== */

function VideoTab({ userId }: { userId: number }) {
  const client = useQueryClient()
  const page = useQuery(videoQuery(userId))
  const [video, setVideo] = useState(false)
  const [audio, setAudio] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!page.data) return
    setVideo(page.data.settings.videoConference)
    setAudio(page.data.settings.withAudio)
  }, [page.data])

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void saveVideo(userId, {
      available_for_videoconference: video ? 'on' : '',
      available_conference_with_audio: audio ? 'on' : '',
    })
      .then(() => {
        toast('Changes saved', 'Success')
        void client.invalidateQueries({ queryKey: ['settings', 'users', 'accounts', userId, 'video'] })
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <form role="form" id="video_conference_settings_form" method="post" onSubmit={submit}>
      <FormRow>
        <CheckField
          id="available_for_videoconference"
          label="Available for video conference"
          className="available_for_videoconference"
          checked={video}
          onChange={setVideo}
        />
        <CheckField
          id="available_conference_with_audio"
          label="Available conference with audio"
          className="available_conference_with_audio"
          checked={audio}
          onChange={setAudio}
        />
      </FormRow>
      <FormActions>
        <button className="btn btn-success" type="submit" disabled={busy}>
          Save Changes
        </button>
      </FormActions>
    </form>
  )
}
